<template>
  <div class="timetable-page">
    <v-card class="rounded-lg elevation-1 bg-white">
      <!-- 1. PAGE HEADER -->
      <v-card-title class="d-flex justify-space-between align-center py-4 px-5 border-bottom">
        <div class="d-flex align-center">
          <v-icon color="primary" size="28" class="me-2">mdi-bus-clock</v-icon>
          <span class="text-h5 font-weight-black text-dark-primary">الجدول الزمني للرحلات</span>
        </div>
        <v-chip color="primary lighten-5" class="primary--text font-weight-bold">
          إجمالي الرحلات: {{ pagination.total || 0 }}
        </v-chip>
      </v-card-title>

      <!-- 2. LIFECYCLE TABS NAV -->
      <v-tabs v-model="active_tab" show-arrows class="my-0 border-bottom" color="primary">
        <v-tab v-for="tab in translatedTabs" :key="tab.idx" class="font-weight-bold">
          <v-icon size="20" class="me-2">{{ tab.icon }}</v-icon>
          <span>{{ tab.title }}</span>
        </v-tab>
      </v-tabs>

      <!-- 3. ADVANCED SERVER-SIDE FILTER BAR -->
      <div class="pa-4 grey lighten-5 border-bottom">
        <v-row dense align="center">
          <!-- Search input -->
          <v-col cols="12" md="4" lg="4">
            <v-text-field
              v-model="filters.search"
              placeholder="ابحث باسم الكابتن أو المسار أو الرخصة..."
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              class="bg-white text-dark-primary"
              @keyup.enter="applyFilters"
              @click:clear="clearSearch"
            ></v-text-field>
          </v-col>

          <!-- Date Quick Preset Chips -->
          <v-col cols="12" md="4" lg="4">
            <div class="d-flex align-center gap-1">
              <v-chip
                small
                filter
                :input-value="filters.date_mode === 'all'"
                :color="filters.date_mode === 'all' ? 'primary' : 'white'"
                :class="filters.date_mode === 'all' ? 'white--text font-weight-bold' : 'text-dark-primary font-weight-medium border-grey'"
                @click="setDateMode('all')"
              >
                الكل
              </v-chip>
              <v-chip
                small
                filter
                :input-value="filters.date_mode === 'today'"
                :color="filters.date_mode === 'today' ? 'primary' : 'white'"
                :class="filters.date_mode === 'today' ? 'white--text font-weight-bold' : 'text-dark-primary font-weight-medium border-grey'"
                @click="setDateMode('today')"
              >
                اليوم
              </v-chip>
              <v-chip
                small
                filter
                :input-value="filters.date_mode === 'week'"
                :color="filters.date_mode === 'week' ? 'primary' : 'white'"
                :class="filters.date_mode === 'week' ? 'white--text font-weight-bold' : 'text-dark-primary font-weight-medium border-grey'"
                @click="setDateMode('week')"
              >
                هذا الأسبوع
              </v-chip>
              <v-chip
                small
                filter
                :input-value="filters.date_mode === 'custom'"
                :color="filters.date_mode === 'custom' ? 'primary' : 'white'"
                :class="filters.date_mode === 'custom' ? 'white--text font-weight-bold' : 'text-dark-primary font-weight-medium border-grey'"
                @click="setDateMode('custom')"
              >
                تاريخ محدد
              </v-chip>
            </div>
          </v-col>

          <!-- Action Buttons -->
          <v-col cols="12" md="4" lg="4" class="d-flex align-center justify-end gap-2">
            <v-btn color="primary" small class="font-weight-bold white--text" :loading="isLoading" @click="applyFilters">
              <v-icon left size="16">mdi-filter</v-icon>
              تصفية
            </v-btn>
            <v-btn outlined color="grey darken-2" small class="font-weight-bold text-dark-primary" @click="resetFilters">
              <v-icon left size="16">mdi-refresh</v-icon>
              إعادة ضبط
            </v-btn>
          </v-col>
        </v-row>

        <!-- Custom Date Range Row -->
        <v-row v-if="filters.date_mode === 'custom'" dense class="mt-2">
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.date_from"
              type="date"
              label="من تاريخ"
              dense
              outlined
              hide-details
              class="bg-white text-dark-primary"
              @change="applyFilters"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.date_to"
              type="date"
              label="إلى تاريخ"
              dense
              outlined
              hide-details
              class="bg-white text-dark-primary"
              @change="applyFilters"
            ></v-text-field>
          </v-col>
        </v-row>
      </div>

      <!-- 4. TIMETABLE DATA TABLE & TABS ITEMS -->
      <v-tabs-items v-model="active_tab">
        <!-- TAB 0: NOT STARTED -->
        <v-tab-item>
          <planned-trips-table
            :planned-trips="plannedTrips"
            :loading="isLoading"
            :show-notification="true"
            @send-notification="sendNotification"
            @open-details="handleOpenDetails"
          ></planned-trips-table>
        </v-tab-item>

        <!-- TAB 1: ON ROUTE / RUNNING -->
        <v-tab-item>
          <planned-trips-table
            :planned-trips="plannedTrips"
            :loading="isLoading"
            :show-start="true"
            :show-notification="true"
            @send-notification="sendNotification"
            @open-details="handleOpenDetails"
          ></planned-trips-table>
        </v-tab-item>

        <!-- TAB 2: COMPLETED -->
        <v-tab-item>
          <planned-trips-table
            :planned-trips="plannedTrips"
            :loading="isLoading"
            :show-start="true"
            :show-end="true"
            @open-details="handleOpenDetails"
          ></planned-trips-table>
        </v-tab-item>
      </v-tabs-items>

      <!-- 5. SERVER-SIDE PAGINATION -->
      <div class="pa-3 border-top d-flex align-center justify-space-between flex-wrap gap-2 grey lighten-5">
        <div class="text-caption font-weight-bold text-dark-secondary">
          عرض الصفحة {{ pagination.current_page }} من {{ pagination.last_page }} (إجمالي {{ pagination.total }} رحلات)
        </div>
        <v-pagination
          v-if="pagination.last_page > 1"
          v-model="pagination.current_page"
          :length="pagination.last_page"
          total-visible="7"
          dense
          color="primary"
          @input="onPageChange"
        ></v-pagination>
      </div>
    </v-card>

    <!-- FULL TRIP DETAILS MODAL -->
    <trip-details-modal
      v-model="showDetailsModal"
      :planned-trip-id="selectedTripId"
    ></trip-details-modal>
  </div>
</template>

<script>
import {
  mdiAccountCheck,
  mdiPlayCircleOutline,
  mdiAirplane,
} from "@mdi/js";

import plannedTripsTable from "./planned-trips-table.vue";
import TripDetailsModal from "./TripDetailsModal.vue";
import auth from "@/services/AuthService";

export default {
  components: {
    plannedTripsTable,
    TripDetailsModal,
  },
  data() {
    return {
      plannedTrips: [],
      isLoading: false,
      active_tab: 0,
      showDetailsModal: false,
      selectedTripId: null,
      filters: {
        search: "",
        date_mode: "all",
        date_from: "",
        date_to: "",
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 15,
      },
      tabs: [
        { idx: 0, title: "not_started", tabKey: "not_started", icon: mdiPlayCircleOutline },
        { idx: 1, title: "on_route", tabKey: "on_route", icon: mdiAirplane },
        { idx: 2, title: "completed", tabKey: "completed", icon: mdiAccountCheck },
      ],
    };
  },
  watch: {
    active_tab: function (newVal) {
      localStorage.tabIdxPlannedTrips = newVal;
      this.pagination.current_page = 1;
      this.loadPlannedTrips();
    },
  },
  computed: {
    translatedTabs() {
      return this.tabs.map((tab) => ({
        ...tab,
        title: this.$t(tab.title),
      }));
    },
    currentTabKey() {
      const tabObj = this.tabs.find((t) => t.idx === this.active_tab);
      return tabObj ? tabObj.tabKey : "not_started";
    },
  },
  mounted() {
    this.active_tab = parseInt(localStorage.tabIdxPlannedTrips) || 0;
    this.loadPlannedTrips();
  },
  methods: {
    loadPlannedTrips() {
      this.isLoading = true;
      const params = {
        tab: this.currentTabKey,
        search: this.filters.search || "",
        date_mode: this.filters.date_mode,
        date_from: this.filters.date_from || "",
        date_to: this.filters.date_to || "",
        page: this.pagination.current_page,
        per_page: this.pagination.per_page,
      };

      axios
        .get(`/planned-trips/timetable-list`, { params })
        .then((response) => {
          this.isLoading = false;
          if (response.data && response.data.data) {
            this.plannedTrips = response.data.data;
            this.pagination.current_page = response.data.current_page;
            this.pagination.last_page = response.data.last_page;
            this.pagination.total = response.data.total;
          } else {
            this.plannedTrips = response.data || [];
          }
        })
        .catch((error) => {
          this.isLoading = false;
          console.error("Error loading timetable planned trips:", error);
          this.$notify({
            title: "Error",
            text: this.$t("error_retrieving_planned_trips"),
            type: "error",
          });
          auth.checkError(error.response ? error.response.data.message : error, this.$router, this.$swal);
        });
    },
    applyFilters() {
      this.pagination.current_page = 1;
      this.loadPlannedTrips();
    },
    resetFilters() {
      this.filters = {
        search: "",
        date_mode: "all",
        date_from: "",
        date_to: "",
      };
      this.pagination.current_page = 1;
      this.loadPlannedTrips();
    },
    clearSearch() {
      this.filters.search = "";
      this.applyFilters();
    },
    setDateMode(mode) {
      this.filters.date_mode = mode;
      if (mode !== "custom") {
        this.filters.date_from = "";
        this.filters.date_to = "";
      }
      this.applyFilters();
    },
    onPageChange(page) {
      this.pagination.current_page = page;
      this.loadPlannedTrips();
    },
    handleOpenDetails(item) {
      this.selectedTripId = item.id;
      this.showDetailsModal = true;
    },
    sendNotification(trip) {
      this.$swal({
        input: "textarea",
        inputPlaceholder: this.$t("enter_notification_message"),
        inputAttributes: {
          "aria-label": this.$t("enter_notification_message"),
        },
        title: this.$t("send_notification"),
        html: this.$t("enter_notification_message"),
        icon: "info",
        showCancelButton: true,
        confirmButtonText: this.$t("send_notification"),
        cancelButtonText: this.$t("cancel"),
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .post(`/planned-trips/notify`, {
              id: trip.id,
              message: result.value,
            })
            .then((response) => {
              this.$notify({
                title: "Success",
                text: this.$t("notification_sent_successfully"),
                type: "success",
              });
              if (response.status == 201)
                this.$swal("Info", response.data.message, "info");
            })
            .catch((error) => {
              this.$notify({
                title: "Error",
                text: this.$t("error_sending_notification"),
                type: "error",
              });
              console.log(error);
              this.$swal("Error", error.response.data.message, "error");
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.timetable-page { color: #111827 !important; }
.text-dark-primary { color: #111827 !important; }
.text-dark-secondary { color: #374151 !important; }
.border-bottom { border-bottom: 1px solid #e5e7eb !important; }
.border-top { border-top: 1px solid #e5e7eb !important; }
.border-grey { border: 1px solid #e5e7eb !important; }
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
</style>
